import { supabase } from '../lib/supabase';
import { s3UploadService } from './s3UploadService';
import { bucketConfigService } from './bucketConfigService';
import { userPreferencesService } from './userPreferencesService';

/**
 * Service for managing user storage folders in Supabase
 * Handles CRUD operations for user-specific S3 folders
 * NOW WITH S3 INTEGRATION - Creates actual folders in S3
 */

class UserStorageFolderService {
  constructor() {
    this.tableName = 'user_storage_folders';
  }

  /**
   * Get all storage folders for the current user
   * @returns {Promise<Array>} List of storage folders
   */
  async getAll() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError) throw authError;
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from(this.tableName)?.select(`
          *,
          bucket_configurations (
            id,
            bucket_name,
            bucket_region,
            public_url
          )
        `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching storage folders:', error);
      throw error;
    }
  }

  /**
   * Get active storage folder for the current user
   * NOW QUERIES BOTH user_storage_folders AND user_preferences FOR COMPLETE SYNC
   * @returns {Promise<Object|null>} Active storage folder or null
   */
  async getActiveFolder() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError) throw authError;
      if (!user) throw new Error('User not authenticated');

      console.log('🔍 Fetching active folder from database...');

      // CRITICAL FIX: Query using BOTH is_active flag AND user_preferences reference
      // First, get user preferences to find active_folder_id
      const { data: prefs, error: prefsError } = await supabase?.from('user_preferences')?.select('active_folder_id')?.eq('user_id', user?.id)?.single();

      if (prefsError && prefsError?.code !== 'PGRST116') {
        console.error('Error fetching user preferences:', prefsError);
      }

      // If active_folder_id exists in preferences, use it as primary source
      if (prefs?.active_folder_id) {
        console.log('📂 Found active_folder_id in user_preferences:', prefs?.active_folder_id);

        const { data, error } = await supabase?.from(this.tableName)?.select(`
            *,
            bucket_configurations (
              id,
              bucket_name,
              bucket_region,
              public_url,
              access_key_id,
              secret_access_key
            )
          `)?.eq('id', prefs?.active_folder_id)?.single();

        if (error && error?.code !== 'PGRST116') throw error;
        
        if (data) {
          console.log('✅ Active folder retrieved via user_preferences:', {
            folderId: data?.id,
            folderPath: data?.folder_path,
            isActive: data?.is_active
          });
          return data;
        }
      }

      // Fallback: Query by is_active flag if preferences didn't have a match
      console.log('📂 Fallback: Querying by is_active flag...');
      const { data, error } = await supabase?.from(this.tableName)?.select(`
          *,
          bucket_configurations (
            id,
            bucket_name,
            bucket_region,
            public_url,
            access_key_id,
            secret_access_key
          )
        `)?.eq('user_id', user?.id)?.eq('is_active', true)?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      
      if (data) {
        console.log('✅ Active folder retrieved via is_active flag:', {
          folderId: data?.id,
          folderPath: data?.folder_path
        });

        // SYNC FIX: If we found a folder by is_active but it's not in preferences, sync it
        if (!prefs?.active_folder_id || prefs?.active_folder_id !== data?.id) {
          console.log('🔧 Syncing active folder to user_preferences...');
          try {
            await userPreferencesService?.updateActiveFolder(data?.id);
          } catch (syncError) {
            console.error('Warning: Failed to sync active folder to preferences:', syncError);
          }
        }

        return data;
      }

      console.log('📂 No active folder found');
      return null;

    } catch (error) {
      console.error('Error fetching active folder:', error);
      throw error;
    }
  }

  /**
   * Create a new storage folder request
   * NOW CREATES ACTUAL FOLDER IN S3
   * @param {Object} folderData - Storage folder data
   * @param {string} folderData.bucket_id - Bucket configuration ID
   * @param {string} folderData.folder_name - Name of the folder
   * @returns {Promise<Object>} Created storage folder
   */
  async create(folderData) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError) throw authError;
      if (!user) throw new Error('User not authenticated');

      // Get user profile for username
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('email, full_name')?.eq('id', user?.id)?.single();

      if (profileError) throw profileError;

      // Create folder path based on username (email prefix)
      const username = profile?.email?.split('@')?.[0] || 'user';
      const folderPath = `${username}/${folderData?.folder_name || 'storage'}`;

      console.log('📁 Creating folder in database and S3...', {
        folderPath: folderPath,
        bucketId: folderData?.bucket_id
      });

      // 1. Create database record first
      const { data, error } = await supabase?.from(this.tableName)?.insert({
          user_id: user?.id,
          bucket_id: folderData?.bucket_id,
          folder_name: folderData?.folder_name || username,
          folder_path: folderPath,
          status: 'pending'
        })?.select()?.single();

      if (error) throw error;

      // 2. Initialize S3 client with bucket credentials
      try {
        // Get bucket configuration
        const bucketConfig = await bucketConfigService?.getById(folderData?.bucket_id);
        
        if (!bucketConfig) {
          throw new Error('Bucket configuration not found');
        }

        console.log('🔌 Initializing S3 client for folder creation...', {
          bucketName: bucketConfig?.bucketName,
          region: bucketConfig?.bucketRegion
        });

        // Initialize S3 client
        await s3UploadService?.initializeClient({
          bucketName: bucketConfig?.bucketName,
          bucketRegion: bucketConfig?.bucketRegion,
          accessKeyId: bucketConfig?.accessKeyId,
          secretAccessKey: bucketConfig?.secretAccessKey,
          publicUrl: bucketConfig?.publicUrl
        });

        // 3. Create physical folder in S3
        console.log('📤 Creating physical folder in S3...', {
          folderPath: folderPath
        });

        const folderCreated = await s3UploadService?.createFolder(folderPath);

        console.log('✅ Folder created successfully in both database and S3', {
          folderId: data?.id,
          folderPath: folderPath,
          s3Result: folderCreated
        });

        // Update status to active now that folder is created
        await supabase?.from(this.tableName)?.update({
            status: 'active'
          })?.eq('id', data?.id);

      } catch (s3Error) {
        console.error('❌ Failed to create folder in S3, but database record exists', s3Error);
        
        // Update status to indicate S3 creation failed
        await supabase?.from(this.tableName)?.update({
            status: 'pending'
          })?.eq('id', data?.id);

        throw new Error(`Folder created in database but S3 creation failed: ${s3Error?.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating storage folder:', error);
      throw error;
    }
  }

  /**
   * Update a storage folder
   * @param {string} folderId - Folder ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated storage folder
   */
  async update(folderId, updates) {
    try {
      const { data, error } = await supabase?.from(this.tableName)?.update(updates)?.eq('id', folderId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating storage folder:', error);
      throw error;
    }
  }

  /**
   * Set a folder as active (automatically deactivates other folders)
   * NOW UPDATES S3 SERVICE ACTIVE FOLDER WITH VERIFICATION AND USER_PREFERENCES SYNC
   * @param {string} folderId - Folder ID to activate
   * @returns {Promise<Object>} Updated storage folder with sync verification
   */
  async setActiveFolder(folderId) {
    try {
      console.log('🔄 Activating folder with complete sync verification...', { folderId });

      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('User not authenticated');

      // Get folder details with bucket config
      const { data: folder, error: folderError } = await supabase?.from(this.tableName)?.select(`
          *,
          bucket_configurations (
            id,
            bucket_name,
            bucket_region,
            public_url,
            access_key_id,
            secret_access_key
          )
        `)?.eq('id', folderId)?.single();

      if (folderError) throw folderError;
      if (!folder) throw new Error('Folder not found');

      // CRITICAL FIX: Update BOTH tables atomically
      
      // 1. Update user_storage_folders to set folder as active
      const { data, error } = await supabase?.from(this.tableName)?.update({ 
          is_active: true,
          status: 'active',
          activated_at: new Date()?.toISOString()
        })?.eq('id', folderId)?.select()?.single();

      if (error) throw error;

      console.log('✅ Active folder saved to user_storage_folders', {
        folderId: data?.id,
        folderPath: data?.folder_path,
        activatedAt: data?.activated_at
      });

      // 2. CRITICAL FIX: Update user_preferences.active_folder_id to sync with user_storage_folders
      try {
        await userPreferencesService?.updateActiveFolder(folderId);
        console.log('✅ Active folder synced to user_preferences table', {
          folderId: folderId,
          userId: user?.id
        });
      } catch (prefsError) {
        console.error('❌ Failed to sync active folder to user_preferences:', prefsError);
        // Don't throw - folder is still active in user_storage_folders
        // But log the sync failure
      }

      // Initialize S3 client with bucket credentials
      const bucketConfig = folder?.bucket_configurations;
      
      if (bucketConfig) {
        console.log('🔌 Updating S3 service active folder...', {
          folderPath: folder?.folder_path,
          bucket: bucketConfig?.bucket_name
        });

        // Initialize S3 client if not already initialized
        await s3UploadService?.initializeClient({
          bucketName: bucketConfig?.bucket_name,
          bucketRegion: bucketConfig?.bucket_region,
          accessKeyId: bucketConfig?.access_key_id,
          secretAccessKey: bucketConfig?.secret_access_key,
          publicUrl: bucketConfig?.public_url
        });

        // Set active folder in S3 service
        s3UploadService?.setActiveFolder(folder?.folder_path);

        console.log('✅ Active folder updated in S3 service', {
          folderPath: folder?.folder_path
        });
      }

      // CRITICAL: Return complete data with verification status
      return {
        ...data,
        syncVerified: true,
        syncTimestamp: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('❌ Error setting active folder:', error);
      throw error;
    }
  }

  /**
   * Verify active folder sync status
   * Confirms that active folder is properly saved in Supabase
   * @param {string} folderId - Folder ID to verify
   * @returns {Promise<Object>} Verification result with sync status
   */
  async verifyActiveFolderSync(folderId) {
    try {
      console.log('🔍 Verifying active folder sync...', { folderId });

      const { data, error } = await supabase?.from(this.tableName)?.select(`
          id,
          folder_path,
          is_active,
          status,
          activated_at,
          bucket_configurations (
            id,
            bucket_name
          )
        `)?.eq('id', folderId)?.eq('is_active', true)?.eq('status', 'active')?.single();

      if (error && error?.code !== 'PGRST116') {
        throw error;
      }

      const syncVerified = !!data && data?.is_active === true;

      console.log(syncVerified ? '✅ Sync verified' : '❌ Sync not verified', {
        folderId: folderId,
        found: !!data,
        isActive: data?.is_active,
        status: data?.status
      });

      return {
        syncVerified: syncVerified,
        folderData: data || null,
        timestamp: new Date()?.toISOString()
      };
    } catch (error) {
      console.error('❌ Error verifying sync:', error);
      return {
        syncVerified: false,
        folderData: null,
        error: error?.message,
        timestamp: new Date()?.toISOString()
      };
    }
  }

  /**
   * Delete a storage folder
   * NOW DELETES PHYSICAL FOLDER FROM S3
   * @param {string} folderId - Folder ID to delete
   * @returns {Promise<void>}
   */
  async delete(folderId) {
    try {
      console.log('🗑️ Deleting folder...', { folderId });

      // Get folder details with bucket config
      const { data: folder, error: folderError } = await supabase?.from(this.tableName)?.select(`
          *,
          bucket_configurations (
            id,
            bucket_name,
            bucket_region,
            public_url,
            access_key_id,
            secret_access_key
          )
        `)?.eq('id', folderId)?.single();

      if (folderError) throw folderError;
      if (!folder) throw new Error('Folder not found');

      // Initialize S3 client with bucket credentials
      const bucketConfig = folder?.bucket_configurations;
      
      if (bucketConfig) {
        try {
          console.log('🔌 Initializing S3 client for folder deletion...', {
            bucket: bucketConfig?.bucket_name,
            folderPath: folder?.folder_path
          });

          // Initialize S3 client
          await s3UploadService?.initializeClient({
            bucketName: bucketConfig?.bucket_name,
            bucketRegion: bucketConfig?.bucket_region,
            accessKeyId: bucketConfig?.access_key_id,
            secretAccessKey: bucketConfig?.secret_access_key,
            publicUrl: bucketConfig?.public_url
          });

          // Delete physical folder from S3
          console.log('📤 Deleting physical folder from S3...', {
            folderPath: folder?.folder_path
          });

          await s3UploadService?.deleteFolder(folder?.folder_path);

          console.log('✅ Folder deleted from S3', {
            folderPath: folder?.folder_path
          });

        } catch (s3Error) {
          console.error('❌ Failed to delete folder from S3', s3Error);
          // Continue with database deletion even if S3 deletion fails
        }
      }

      // Delete from database
      const { error } = await supabase?.from(this.tableName)?.delete()?.eq('id', folderId);

      if (error) throw error;

      console.log('✅ Folder deleted from database', { folderId });

    } catch (error) {
      console.error('Error deleting storage folder:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time changes for user's storage folders
   * @param {string} userId - User ID
   * @param {Function} callback - Callback function for changes
   * @returns {Object} Subscription channel
   */
  subscribeToChanges(userId, callback) {
    const channel = supabase?.channel('user_storage_folders_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: this.tableName,
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return channel;
  }

  /**
   * Unsubscribe from real-time changes
   * @param {Object} channel - Subscription channel
   */
  unsubscribeFromChanges(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
}

// Export singleton instance
export const userStorageFolderService = new UserStorageFolderService();
export default userStorageFolderService;